import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "https://ims.holdthechild.net/api/documents";

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to handle API responses
const handleApiResponse = (response) => {
  if (response.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
    if (Array.isArray(response.data.documents)) return response.data.documents;
    if (Array.isArray(response.data.data)) return response.data.data;
    if (Array.isArray(response.data.results)) return response.data.results;
  }
  return response.data;
};

// Get signed upload URL from backend
export const getUploadUrl = async ({ filename, filetype, entityId, entityType, category }) => {
  try {
    const res = await axios.post(`${API_BASE}/upload-url`, {
      filename,
      filetype,
      entityId,
      entityType,
      category,
    }, {
      headers: getAuthHeaders()
    });
    return res.data; // { uploadUrl, fileUrl, key }
  } catch (error) {
    console.error('Error getting upload URL:', error);
    throw error;
  }
};

// Upload file directly to S3 using signed URL
export const uploadFileToS3 = async (uploadUrl, file, filetype) => {
  try {
    const res = await axios.put(uploadUrl, file, {
      headers: { "Content-Type": filetype },
    });
    return res;
  } catch (error) {
    console.error('Error uploading file to S3:', error);
    throw error;
  }
};

// Save metadata in backend
export const saveDocumentMetadata = async (docData) => {
  try {
    const res = await axios.post(`${API_BASE}`, docData, {
      headers: getAuthHeaders()
    });
    return res.data;
  } catch (error) {
    console.error('Error saving document metadata:', error);
    throw error;
  }
};

// Enhanced fetch documents function
export const getDocuments = async (entityType, entityId) => {
  const url = `${API_BASE}/Staff/${entityId}`;
  try {
    const res = await axios.get(url, { 
      headers: getAuthHeaders(),
      timeout: 30000 // 30 second timeout
    });
    return handleApiResponse(res);
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

// Fetch all user-related documents (enhanced)
export const getAllUserDocuments = async (userId) => {
  const allDocuments = [];
  const documentIds = new Set();
  const headers = getAuthHeaders();

  // Array of endpoint patterns to try
  const endpointPatterns = [
    `${API_BASE}/Staff/${userId}`,           // Your existing endpoint
    `${API_BASE}/user/${userId}`,            // Alternative user endpoint
    `${API_BASE}/staff/${userId}`,           // Alternative staff endpoint
    `${API_BASE}/assigned/${userId}`,        // Assigned documents
    `${API_BASE}/shared/${userId}`,          // Shared documents
  ];

  for (const endpoint of endpointPatterns) {
    try {
      const response = await axios.get(endpoint, { 
        headers,
        timeout: 30000, // CHANGED FROM 5000 TO 30000
        validateStatus: (status) => status < 500 // Don't throw on 4xx errors
      });

      if (response.status === 200) {
        const docs = handleApiResponse(response);
        
        if (Array.isArray(docs)) {
          docs.forEach(doc => {
            const docId = doc._id || doc.id;
            if (docId && !documentIds.has(docId)) {
              documentIds.add(docId);
              
              // Add metadata about document source
              let source = 'owned';
              if (endpoint.includes('/assigned/')) source = 'assigned';
              else if (endpoint.includes('/shared/')) source = 'shared';
              
              allDocuments.push({
                ...doc,
                source,
                isOwned: doc.uploadedBy === userId || doc.staffId === userId,
                isAssigned: doc.assignedTo?.includes(userId) || doc.assignedStaff?.includes(userId) || false,
                isShared: doc.sharedWith?.includes(userId) || false,
              });
            }
          });
        }
      }
    } catch (error) {
      // Log but don't throw - some endpoints might not exist
      console.log(`Endpoint ${endpoint} not available:`, error.message);
    }
  }

  // If no documents found, try the base endpoint and filter
  if (allDocuments.length === 0) {
    try {
      const response = await axios.get(API_BASE, { 
        headers,
        timeout: 30000 // CHANGED FROM 10000 TO 30000
      });
      
      const allDocs = handleApiResponse(response);
      
      if (Array.isArray(allDocs)) {
        allDocs.forEach(doc => {
          const docId = doc._id || doc.id;
          const isOwned = doc.uploadedBy === userId || doc.staffId === userId;
          const isAssigned = doc.assignedTo?.includes(userId) || doc.assignedStaff?.includes(userId) || false;
          const isShared = doc.sharedWith?.includes(userId) || false;
          
          if ((isOwned || isAssigned || isShared) && docId && !documentIds.has(docId)) {
            documentIds.add(docId);
            allDocuments.push({
              ...doc,
              source: isOwned ? 'owned' : isAssigned ? 'assigned' : 'shared',
              isOwned,
              isAssigned,
              isShared,
            });
          }
        });
      }
    } catch (error) {
      console.error('Error fetching all documents for filtering:', error);
    }
  }

  // Sort by creation date (newest first)
  allDocuments.sort((a, b) => {
    const dateA = new Date(a.createdAt || a.uploadDate || 0);
    const dateB = new Date(b.createdAt || b.uploadDate || 0);
    return dateB - dateA;
  });

  return allDocuments;
};

// Get documents by category
export const getDocumentsByCategory = async (category, userId) => {
  try {
    const allDocs = await getAllUserDocuments(userId);
    return allDocs.filter(doc => doc.category === category);
  } catch (error) {
    console.error('Error fetching documents by category:', error);
    throw error;
  }
};

// Search documents
export const searchDocuments = async (query, userId) => {
  try {
    const allDocs = await getAllUserDocuments(userId);
    const searchTerm = query.toLowerCase();
    
    return allDocs.filter(doc => 
      doc.originalName?.toLowerCase().includes(searchTerm) ||
      doc.category?.toLowerCase().includes(searchTerm) ||
      doc.description?.toLowerCase().includes(searchTerm)
    );
  } catch (error) {
    console.error('Error searching documents:', error);
    throw error;
  }
};

// Delete document
export const deleteDocument = async (documentId) => {
  try {
    const res = await axios.delete(`${API_BASE}/${documentId}`, {
      headers: getAuthHeaders()
    });
    return res.data;
  } catch (error) {
    console.error('Error deleting document:', error);
    throw error;
  }
};

// Update document metadata
export const updateDocumentMetadata = async (documentId, updateData) => {
  try {
    const res = await axios.patch(`${API_BASE}/${documentId}`, updateData, {
      headers: getAuthHeaders()
    });
    return res.data;
  } catch (error) {
    console.error('Error updating document metadata:', error);
    throw error;
  }
};

// Get document statistics
export const getDocumentStats = async (userId) => {
  try {
    const allDocs = await getAllUserDocuments(userId);
    
    const stats = {
      total: allDocs.length,
      owned: allDocs.filter(doc => doc.isOwned).length,
      assigned: allDocs.filter(doc => doc.isAssigned).length,
      shared: allDocs.filter(doc => doc.isShared).length,
      categories: {},
      recentCount: 0, // Documents from last 7 days
    };

    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

    allDocs.forEach(doc => {
      // Category stats
      const category = doc.category || 'Uncategorized';
      if (!stats.categories[category]) {
        stats.categories[category] = { total: 0, owned: 0, assigned: 0, shared: 0 };
      }
      stats.categories[category].total++;
      if (doc.isOwned) stats.categories[category].owned++;
      if (doc.isAssigned) stats.categories[category].assigned++;
      if (doc.isShared) stats.categories[category].shared++;

      // Recent documents count
      const docDate = new Date(doc.createdAt || doc.uploadDate);
      if (docDate > oneWeekAgo) {
        stats.recentCount++;
      }
    });

    return stats;
  } catch (error) {
    console.error('Error getting document stats:', error);
    throw error;
  }
};
